import React from "react";
import { Plus, Eye, EyeOff, Package, Monitor } from "lucide-react";
import LoadingScreen from "../../../utils/LoadingScreen";

const CatalogueCategory = ({
  activeTab,
  pageLoading,
  filteredCategories,
  getCategoryKey,
  expandedCategories,
  toggleCategory,
  setSelectedCategory,
  setShowCustomizationModal,
  setSelectedCustomizations,
  getEffectiveCategoryVisibleValue,
  handleCategoryVisibilityToggle,
  optimisticStocks,
  optimisticVisible,
  getVariantLabel,
  handleVariantStockToggle,
  handleVariantVisibleToggle,
  catalogue,
  setViewModalItem,
  setViewModalTab,
  packagingConfig,
  handlePackagingToggle,
  FoodTypeBadge,
  setSelectedItem,
  setShowTableVariantModal,
  rawItemName,
  setRawItemName,
  handleAddRawItem,
  addingRawItem,
  rawInventoryLoading,
  filteredRawInventory,
  getSelectedCustomizationIds,
  // 👇 NEW
  handleItemShowingStatusToggle,
  optimisticShowing, // optional optimistic map: { [item_id]: "Y" | "N" }
}) => {
  return (
    <>
      {/* CATEGORY LIST */}
      {activeTab !== "raw-inventory" &&
        (pageLoading ? (
          <div className="flex justify-center items-center py-20">
            <LoadingScreen text="Loading Catalogue..." />
          </div>
        ) : (
          <div className="space-y-4 montserrat-font-1">
            {filteredCategories.map((category) => {
              const catKey = getCategoryKey(category);
              const isOpen = expandedCategories.includes(catKey);

              return (
                <div key={catKey} className="bg-white rounded-xl shadow p-4">
                  <div
                    onClick={() => toggleCategory(catKey)}
                    className="flex justify-between items-center cursor-pointer"
                  >
                    <h2 className="font-semibold text-lg">
                      {category.category_name}
                    </h2>

                    <div className="flex items-center gap-3">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();

                          setSelectedCategory(category);

                          const selectedIds =
                            getSelectedCustomizationIds(category);

                          setSelectedCustomizations(selectedIds);

                          setShowCustomizationModal(true);
                        }}
                        className="bg-emerald-500 hover:bg-emerald-600 text-white px-3 py-1 rounded-md text-sm"
                      >
                        Customize
                      </button>

                      {(() => {
                        const isCategoryVisible =
                          getEffectiveCategoryVisibleValue(category) === "Y";

                        return (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCategoryVisibilityToggle(category);
                            }}
                            className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold transition ${isCategoryVisible
                                ? "border-blue-200 bg-blue-50 text-blue-700"
                                : "border-gray-200 bg-gray-100 text-gray-600"
                              }`}
                            title={
                              isCategoryVisible
                                ? "Category Visible"
                                : "Category Hidden"
                            }
                          >
                            <span
                              className={`relative inline-flex h-5 w-9 items-center rounded-full transition ${isCategoryVisible
                                  ? "bg-blue-500"
                                  : "bg-gray-300"
                                }`}
                            >
                              <span
                                className={`inline-block h-4 w-4 rounded-full bg-white shadow transition ${isCategoryVisible
                                    ? "translate-x-4"
                                    : "translate-x-1"
                                  }`}
                              />
                            </span>

                            <span>{isCategoryVisible ? "ON" : "OFF"}</span>
                          </button>
                        );
                      })()}

                      <div className="relative group">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleCategory(catKey);
                          }}
                          className="w-8 h-8 flex items-center justify-center rounded-full bg-green-50 hover:bg-green-100 text-green-500 transition"
                        >
                          {isOpen ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                        <span className="absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap text-xs border bg-white text-gray-800 px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition">
                          {isOpen ? "Hide" : "Show"}
                        </span>
                      </div>
                    </div>
                  </div>

                  {isOpen && (
                    <div className="mt-4 space-y-3 montserrat-font-1">
{category.menu_items.map((item) => {
  const hasVariants =
    Array.isArray(item.prices) && item.prices.length > 1;

  const rawStatus =
    optimisticShowing?.[item.item_id] !== undefined
      ? optimisticShowing[item.item_id]
      : item.item_showing_status === "Y"
        ? "Y"
        : "N";

  const isPosOnInUi = rawStatus === "N"; // UI POS ON → dull left side only

  return (
    <div
      key={item.item_id}
      className={`flex items-center justify-between p-3 border rounded-lg transition ${
        isPosOnInUi ? "bg-gray-100 border-gray-200" : "bg-gray-50"
      }`}
    >
      {/* LEFT – dull when POS ON */}
      <div
        className={`flex items-center gap-4 min-w-0 ${
          isPosOnInUi ? "opacity-50" : ""
        }`}
      >
        <img
          src={item.photo || "/placeholder.png"}
          alt={item.item_name || "Item"}
          loading="lazy"
          className="w-16 h-16 rounded-lg object-cover bg-gray-100"
        />
        <div className="min-w-0">
                                <div className="flex items-center gap-2">
                                  <p className="font-semibold text-gray-900 truncate">
                                    {item.item_name}
                                  </p>

                                  <FoodTypeBadge item={item} />
                                </div>

                                <div className="mt-2 flex flex-wrap gap-2">
                                  {(item.prices || [])
                                    .slice(0, 2)
                                    .map((price) => {
                                      const variantId = String(
                                        price.id ||
                                        price.row_id ||
                                        price.size_id ||
                                        "",
                                      );

                                      const isStockOn =
                                        optimisticStocks[variantId] !==
                                          undefined
                                          ? optimisticStocks[variantId] === "Y"
                                          : price.inStock === "Y";

                                      const isVisibleOn =
                                        optimisticVisible[variantId] !==
                                          undefined
                                          ? optimisticVisible[variantId] === "Y"
                                          : price.visibility === "Y";

                                      return (
                                        <div
                                          key={variantId}
                                          className="rounded-lg border bg-white px-3 py-2 min-w-[240px]"
                                        >
                                          <div className="flex items-center justify-between gap-3">
                                            <span className="text-xs font-semibold text-gray-700 truncate">
                                              {getVariantLabel(price)}
                                            </span>

                                            <div className="flex flex-wrap justify-end gap-1">
                                              <span
                                                className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${isStockOn
                                                    ? "bg-green-100 text-green-700"
                                                    : "bg-red-100 text-red-700"
                                                  }`}
                                              >
                                                {isStockOn
                                                  ? "Stock ON"
                                                  : "Stock OFF"}
                                              </span>

                                              <span
                                                className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${isVisibleOn
                                                    ? "bg-blue-100 text-blue-700"
                                                    : "bg-gray-100 text-gray-600"
                                                  }`}
                                              >
                                                {isVisibleOn
                                                  ? "Visible ON"
                                                  : "Visible OFF"}
                                              </span>
                                            </div>
                                          </div>

                                          <div className="mt-2 grid grid-cols-3 gap-1 text-[11px]">
                                            <span className="bg-gray-100 rounded px-2 py-1 text-center">
                                              S ₹
                                              {price.price ||
                                                price.selling_price ||
                                                0}
                                            </span>
                                            <span className="bg-gray-100 rounded px-2 py-1 text-center">
                                              HD ₹
                                              {price.home_delivery_price ||
                                                price.homeDeliveryPrice ||
                                                0}
                                            </span>
                                            <span className="bg-gray-100 rounded px-2 py-1 text-center">
                                              TA ₹
                                              {price.takeaway_price ||
                                                price.takewayPrice ||
                                                price.takeawayPrice ||
                                                0}
                                            </span>
                                          </div>

                                          <div className="mt-3 flex items-center justify-between gap-3 border-t pt-2">
                                            {/* Stock Toggle */}
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleVariantStockToggle(
                                                  item,
                                                  price,
                                                );
                                              }}
                                              className={`flex items-center gap-2 text-[11px] font-semibold ${isStockOn
                                                  ? "text-green-700"
                                                  : "text-red-600"
                                                }`}
                                            >
                                              <span
                                                className={`relative inline-flex h-5 w-9 items-center rounded-full transition ${isStockOn
                                                    ? "bg-green-500"
                                                    : "bg-gray-300"
                                                  }`}
                                              >
                                                <span
                                                  className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition ${isStockOn
                                                      ? "translate-x-4"
                                                      : "translate-x-1"
                                                    }`}
                                                />
                                              </span>
                                              Stock
                                            </button>

                                            {/* Visible Toggle */}
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleVariantVisibleToggle(
                                                  item,
                                                  price,
                                                );
                                              }}
                                              className={`flex items-center gap-2 text-[11px] font-semibold ${isVisibleOn
                                                  ? "text-blue-700"
                                                  : "text-gray-500"
                                                }`}
                                            >
                                              <span
                                                className={`relative inline-flex h-5 w-9 items-center rounded-full transition ${isVisibleOn
                                                    ? "bg-blue-500"
                                                    : "bg-gray-300"
                                                  }`}
                                              >
                                                <span
                                                  className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition ${isVisibleOn
                                                      ? "translate-x-4"
                                                      : "translate-x-1"
                                                    }`}
                                                />
                                              </span>
                                              Visible
                                            </button>
                                          </div>
                                        </div>
                                      );
                                    })}

                                  {(item.prices || []).length > 2 && (
                                    <button
                                      onClick={() => {
                                        const parentCategory = catalogue.find(
                                          (cat) =>
                                            cat.menu_items.some(
                                              (i) => i.item_id === item.item_id,
                                            ),
                                        );
                                        setViewModalItem({
                                          ...item,
                                          category_name:
                                            parentCategory?.category_name || "",
                                          cat_id: parentCategory?.cat_id || "",
                                        });
                                        setViewModalTab("details");
                                      }}
                                      className="rounded-lg border border-dashed bg-white px-4 py-2 text-xs font-semibold text-blue-600 hover:bg-blue-50"
                                    >
                                      +{item.prices.length - 2} more prices
                                    </button>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* RIGHT ACTIONS */}
                            {/* RIGHT ACTIONS */}
                            <div className="flex items-center montserrat-font-1 gap-2 border bg-white px-3 py-2 rounded-full">
                              {/* Package */}
                              {packagingConfig?.packagingItemWise === "Y" && (
                                <div className="relative group">
                                  <button
                                    onClick={() => handlePackagingToggle(item)}
                                    className={`w-9 h-9 flex items-center justify-center rounded-full text-white transition
              ${item.packaging_charge === "Y"
                                        ? "bg-green-500 hover:bg-green-600"
                                        : "bg-red-500 hover:bg-red-600"
                                      }
            `}
                                  >
                                    <Package size={16} />
                                  </button>

                                  <span className="absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap text-xs border bg-white text-gray-800 px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition">
                                    {item.packaging_charge === "Y"
                                      ? "Packaging ON"
                                      : "Packaging OFF"}
                                  </span>
                                </div>
                              )}
                              {/* Show on POS – inverted display */}
                              {(() => {
          const isShowingOnPos = rawStatus === "N";

          return (
            <div className="relative group">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleItemShowingStatusToggle?.(item);
                }}
                className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
                  isShowingOnPos
                    ? "border-blue-200 bg-blue-50 text-blue-700"
                    : "border-gray-200 bg-gray-100 text-gray-600"
                }`}
                title={isShowingOnPos ? "POS ON" : "POS OFF"}
              >
                <span
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition ${
                    isShowingOnPos ? "bg-blue-500" : "bg-gray-300"
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 rounded-full bg-white shadow transition ${
                      isShowingOnPos ? "translate-x-4" : "translate-x-1"
                    }`}
                  />
                </span>
                <span>{isShowingOnPos ? "POS ON" : "POS OFF"}</span>
              </button>
              <span className="absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap text-xs border bg-white text-gray-800 px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition">
                {isShowingOnPos ? "Hide from POS" : "Show on POS"}
              </span>
            </div>
          );
        })()}

                              {/* View */}
                              {/* View */}
                              <div className="relative group">
                                <button
                                  onClick={() => {
                                    const parentCategory = catalogue.find((cat) =>
                                      cat.menu_items.some((i) => i.item_id === item.item_id),
                                    );
                                    setViewModalItem({
                                      ...item,
                                      category_name: parentCategory?.category_name || "",
                                      cat_id: parentCategory?.cat_id || "",
                                    });
                                    setViewModalTab("details");
                                  }}
                                  className="w-9 h-9 flex items-center justify-center rounded-full bg-green-500 text-white hover:bg-green-600"
                                >
                                  <Eye size={16} />
                                </button>
                                <span className="absolute -top-8 left-1/2 -translate-x-1/2 text-xs border bg-white text-gray-800 px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition">
                                  View
                                </span>
                              </div>



                              {/* Add Variant */}
                              <div className="relative group montserrat-font-1">
                                <button
                                  onClick={() => {
                                    setSelectedItem(item);
                                    setShowTableVariantModal(true);
                                  }}
                                  className="w-9 h-9 flex items-center justify-center rounded-full bg-green-500 text-white hover:bg-green-600"
                                >
                                  <Plus size={16} />
                                </button>
                                <span className="absolute -top-8 left-1/2 -translate-x-1/2 text-xs border bg-white text-gray-800 px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition whitespace-nowrap">
                                  Add Variant
                                </span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}

      {activeTab === "raw-inventory" && (
        <div className="bg-white p-4 rounded-xl shadow montserrat-font-1">
          <h2 className="text-lg font-semibold mb-4">Raw Inventory</h2>

          <div className="flex gap-3 mb-4">
            <input
              type="text"
              value={rawItemName}
              onChange={(e) => setRawItemName(e.target.value)}
              placeholder="Enter raw item name"
              className="border rounded-lg px-3 py-2 w-64"
            />
            <button
              onClick={handleAddRawItem}
              disabled={addingRawItem}
              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
            >
              {addingRawItem ? "Adding..." : "Add Raw Item"}
            </button>
          </div>

          {rawInventoryLoading ? (
            <div className="text-center py-10 text-gray-500">Loading...</div>
          ) : filteredRawInventory.length === 0 ? (
            <div className="text-center py-10 text-gray-500">
              No Raw Inventory Found
            </div>
          ) : (
            <div className="overflow-x-auto">
              <div className="max-h-[500px] overflow-y-auto border border-green-200 rounded-lg">
                <table className="min-w-full">
                  <thead className="sticky top-0 z-10 bg-green-100 text-left text-sm text-gray-600">
                    <tr>
                      <th className="px-4 py-3 border-b">#</th>
                      <th className="px-4 py-3 border-b">ID</th>
                      <th className="px-4 py-3 border-b">Item Name</th>
                    </tr>
                  </thead>

                  <tbody className="text-sm text-gray-700">
                    {filteredRawInventory.map((item, index) => (
                      <tr key={index} className="hover:bg-green-50 transition">
                        <td className="px-4 py-3 border-b">{index + 1}</td>
                        <td className="px-4 py-3 border-b">{item.id || "-"}</td>
                        <td className="px-4 py-3 border-b font-medium">
                          {item.item_name || item.name || "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab !== "raw-inventory" && filteredCategories.length === 0 && (
        <div className="text-center text-gray-500 mt-10 montserrat-font-1">
          {activeTab === "out-of-stock"
            ? "All items are in stock!"
            : activeTab === "invisible"
              ? "No invisible items found"
              : "No items found"}
        </div>
      )}
    </>
  );
};

export default CatalogueCategory;
